<template>
  <div>
    <div class="admin-users-toolbar">
      <div v-if="allows_crud" class="toolbar-left ml-3">
        <ButtonMedium
          :tooltip_text="$t('toolbar.adminUsers.addUser')"
          :text_button="$t('reusable.common.add')"
          :click_button="() => $bvModal.show(`new-user-modal`)"
          :icon="'plus'"
          :variant="'primary'"
        >
        </ButtonMedium>
      </div>
      <div :class="{ 'ml-3': !allows_crud }" class="toolbar-filter">
        <div class="filter-label">
          <b-icon icon="funnel" class="filter-icon"></b-icon>
          <span>{{ $t("toolbar.adminUsers.filterByRole") }}</span>
          <span
            v-if="selected_roles.length > 0"
            class="filter-clear"
            @click="selected_roles = []"
          >
            <b-icon icon="x-circle-fill"></b-icon>
            {{ $t("toolbar.adminUsers.clearFilter") }}
          </span>
        </div>
        <v-select
          v-model="selected_roles"
          :options="roles_with_superuser"
          :selectable="(r) => !selected_roles.includes(r.id)"
          label="position_name"
          track-by="id"
          :reduce="(r) => r.id"
          multiple
          :placeholder="$t('toolbar.adminUsers.selectRoles')"
          class="role-select"
        >
          <template #no-options>{{
            $t("toolbar.adminUsers.noRolesAvailable")
          }}</template>
        </v-select>
      </div>
      <div class="mr-3">
        <ButtonMedium
          :tooltip_text="
            show_inactive
              ? $t('toolbar.adminUsers.activeUsers')
              : $t('toolbar.adminUsers.inactiveUsers')
          "
          :text_button="
            show_inactive
              ? $t('toolbar.adminUsers.activeUsers')
              : $t('toolbar.adminUsers.inactiveUsers')
          "
          :click_button="() => (show_inactive = !show_inactive)"
          :variant="'primary'"
        >
          <template #icon>
            <b-icon
              :icon="show_inactive ? 'person-check-fill' : 'person-x-fill'"
            ></b-icon>
          </template>
        </ButtonMedium>
      </div>
    </div>
    <NewGenericBTable
      ref="genericTable"
      :key="generic_table_key"
      :fields="fields"
      :pre_current_page="current_page"
      :url="'/authentication/user/'"
      :pre_multiple_search="current_tag"
      :pre_filter_tags="combined_filter_tags"
      @current_page="currentPage"
      @current_tag="currentTag"
    >
      <template #cell(avatar)="data">
        <div class="avatar-cell">
          <b-avatar
            :id="`user-avatar-${data.item.id}`"
            size="2rem"
            :text="`${
              data.item.first_name.charAt(0) + data.item.last_name.charAt(0)
            }`"
            :variant="data.item.is_active === false ? 'secondary' : 'info'"
          ></b-avatar>
          <b-badge
            v-if="data.item.is_active === false"
            variant="warning"
            class="inactive-badge"
            >Inactivo</b-badge
          >
        </div>
      </template>
      <template #cell(is_professor)="data">
        {{ getRole(data.item) }}
      </template>
      <template #cell(actions)="data">
        <div v-if="!allows_crud" class="noprint">
          <ButtonSmall
            tabindex="-1"
            :click_button="() => openUserInfoModal(data.item.id)"
            :tooltip_text="$t('toolbar.adminUsers.userInformation')"
            :icon="'eye'"
          ></ButtonSmall>
          <b-modal
            :id="`user-info-modal-${data.item.id}`"
            :title="$t('toolbar.adminUsers.userInformation')"
            size="lg"
            hide-footer
          >
            <UserForm
              :User="data.item"
              :IdentifierTypes="identifier_types"
              :Roles="roles"
              :allows_crud="allows_crud"
            ></UserForm>
          </b-modal>
        </div>
        <div v-if="allows_crud">
          <ButtonSmall
            v-if="data.item.user_data == null || data.item.user_data"
            tabindex="-1"
            :click_button="() => sendMail(data.item)"
            :tooltip_text="$t('toolbar.adminUsers.sendEmailToUser')"
            :disabled_button="deleting_user_id === data.item.id"
          >
            <template #icon>
              <template
                v-if="
                  data.item.user_data == null ||
                  (data.item.user_data &&
                    !data.item.user_data.creation_email_sent)
                "
              >
                <b-icon icon="envelope" scale="1"></b-icon
              ></template>
              <template
                v-else-if="
                  data.item.user_data && data.item.user_data.creation_email_sent
                "
              >
                <b-icon icon="envelope-open" scale="1"></b-icon>
              </template>
            </template>
          </ButtonSmall>
          <!-- @click="openUserInfoModal(data.item.id)" -->
          <ButtonSmall
            tabindex="-1"
            :click_button="
              () => $bvModal.show(`edit-user-modal-${data.item.id}`)
            "
            :tooltip_text="$t('toolbar.adminUsers.editUser')"
            :icon="'square'"
            :disabled_button="deleting_user_id === data.item.id"
          ></ButtonSmall>
          <ButtonSmall
            tabindex="-1"
            :click_button="
              () => $bvModal.show(`edit-user-permission-modal-${data.item.id}`)
            "
            :tooltip_text="$t('toolbar.adminUsers.manageUserPermissions')"
            :disabled_button="
              deleting_user_id === data.item.id ||
              !(
                ![6, 7].includes(data.item.groups[0]) &&
                !data.item.is_superuser &&
                data.item.groups.length > 0
              )
            "
          >
            <template #icon>
              <b-icon icon="person-lines-fill"></b-icon>
            </template>
          </ButtonSmall>
          <!-- Copiar Permisos -->
          <ButtonSmall
            tabindex="-1"
            :click_button="() => openModalCopyPermission(data.item)"
            :tooltip_text="$t('toolbar.adminUsers.copyPermissions')"
            :disabled_button="
              deleting_user_id === data.item.id ||
              !(
                !['PROFESORES', 'ESTUDIANTES'].some((group) =>
                  data.item.group_names.includes(group)
                ) &&
                !data.item.is_superuser &&
                data.item.groups.length > 0 &&
                group_count_users.some(
                  (x) =>
                    data.item.group_names.includes(x.name) && x.user_count > 1
                )
              )
            "
          >
            <template #icon>
              <b-icon class="btn-actions" icon="files"></b-icon>
            </template>
          </ButtonSmall>
          <!-- Desactivar / Activar usuario -->
          <div style="display: inline-block">
            <ButtonSmall
              tabindex="-1"
              :click_button="() => askForDeactivateUser(data.item)"
              :tooltip_text="
                user.id == data.item.id
                  ? $t('toolbar.adminUsers.cannotModifyOwnStatus')
                  : data.item.is_active === false
                  ? $t('toolbar.adminUsers.activateUser')
                  : $t('toolbar.adminUsers.deactivateUser')
              "
              :disabled_button="user.id == data.item.id"
            >
              <template #icon>
                <b-icon
                  :icon="
                    data.item.is_active === false
                      ? 'person-check-fill'
                      : 'person-x-fill'
                  "
                  variant="warning"
                ></b-icon>
              </template>
            </ButtonSmall>
          </div>
          <!-- Eliminar usuario -->
          <ButtonSmall
            tabindex="-1"
            :click_button="() => askForDeleteUser(data.item.id)"
            :tooltip_text="
              userIsInSection(data.item)
                ? 'No se puede eliminar un usuario asociado a una sección. Debe desactivarlo.'
                : user.id == data.item.id
                ? $t('toolbar.adminUsers.cannotDeleteCurrentUser')
                : $t('toolbar.adminUsers.deleteUser')
            "
            :disabled_button="
              user.id == data.item.id ||
              deleting_user_id === data.item.id ||
              userIsInSection(data.item)
            "
            :icon="'trash'"
          ></ButtonSmall>
        </div>
        <b-modal
          :id="`edit-user-permission-modal-${data.item.id}`"
          :title="
            $t('reusable.common.editModalTitle', {
              entity: $t('toolbar.adminUsers.roles'),
            })
          "
          size="xl"
          hide-footer
        >
          <AccessForm
            :User="data.item"
            :IdentifierTypes="identifier_types"
            :Roles="roles"
            :allows_crud="allows_crud"
          ></AccessForm>
        </b-modal>
        <b-modal
          :id="`copy-user-permission-modal-${data.item.id}`"
          :title="$t('toolbar.adminUsers.copyPermissions')"
          size="md"
          hide-footer
        >
          <b-form-group
            :label="$t('toolbar.adminUsers.users')"
            label-for="name-input"
            label-cols="0"
            label-cols-sm="3"
          >
            <b-form-select
              id="name-select"
              name="name-select"
              v-model="selected_user"
              value-field="id"
              text-field="name"
            >
              <b-form-select-option
                v-for="user in filterUserByGroups(data.item)"
                :key="user.id"
                :value="user.id"
              >
                {{ user.first_name }} {{ user.last_name }} -
                {{ user.email }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
          <div class="col" style="text-align: right">
            <b-button
              class="mr-1"
              size="sm"
              :disabled="!selected_user"
              @click="copyTableAccess(selected_user, data.item.id)"
              >{{ $t("reusable.common.save") }}</b-button
            >
          </div>
        </b-modal>
        <b-modal
          :id="`edit-user-modal-${data.item.id}`"
          :title="$t('toolbar.adminUsers.editUser')"
          size="lg"
          hide-footer
        >
          <UserForm
            :User="data.item"
            :IdentifierTypes="identifier_types"
            :Roles="roles"
            :allows_crud="allows_crud"
            @updated="slotCloseModal"
          ></UserForm>
        </b-modal>
      </template>
    </NewGenericBTable>
    <div>
      <b-modal
        id="new-user-modal"
        :title="$t('toolbar.adminUsers.createUser')"
        size="lg"
        hide-footer
      >
        <UserForm
          :IdentifierTypes="identifier_types"
          :Roles="roles"
          :allows_crud="allows_crud"
          @created="slotCreatedUser"
        ></UserForm>
      </b-modal>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "AdminUsersTable",
  components: {
    vSelect,
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    UserForm: () => import("./UserForm.vue"),
    AccessForm: () => import("./AccessForm.vue"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  props: {
    allows_crud: {
      type: Boolean,
    },
    identifier_types: {
      type: Array,
      required: true,
    },
    roles: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selected_user: null,
      selected_roles: [],
      show_inactive: false,
      deleting_user_id: null,
      current_page: 1,
      generic_table_key: 0,
      current_tag: [],
      group_count_users: [],
      filtered_users: [], // Copiar Permisos
      fields: [
        {
          key: "avatar",
          label: "",
          display_column: true,
        },
        {
          key: "first_name",
          label: "Nombres",
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "last_name",
          label: "Apellidos",
          sortable: true,
          display_column: true,
          sortDirection: "desc",
          tdClass: "text-left",
        },
        {
          key: "email",
          label: "E-mail",
          sortable: true,
          display_column: true,
          class: "text-left",
        },
        {
          key: "is_professor",
          label: "Rol",
          display_column: true,
          class: "text-left",
        },
        { key: "actions", label: "", class: "noprint", display_column: true },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      users: names.USERS,
    }),
    roles_with_superuser() {
      return [
        {
          id: "superuser",
          position_name: this.$t("toolbar.adminUsers.superUser"),
        },
        ...this.roles,
      ];
    },
    role_filter_tags() {
      const tags = [];
      const superuser_selected = this.selected_roles.includes("superuser");
      const real_roles = this.selected_roles.filter((id) => id !== "superuser");
      if (superuser_selected) tags.push("is_superuser=true");
      if (real_roles.length > 0)
        tags.push(`access__role__id__in=${real_roles.join(",")}`);
      return tags;
    },
    combined_filter_tags() {
      const tags = [...this.role_filter_tags];
      tags.push(this.show_inactive ? "is_active=false" : "is_active=true");
      return tags;
    },
  },
  methods: {
    // changeInputSearch(input_search) {
    //   this.input_search = input_search;
    // },
    sendMail(user) {
      this.$swal.fire({
        title: "Cargando...",
        onBeforeOpen: () => {
          this.$swal.showLoading();
        },
      });
      this.$restful
        .Get(`/authentication/send_welcome_email_to_user/?user=${user.id}`)
        .then(() => {
          this.$swal.fire({
            type: "success",
            title: this.$t("toolbar.adminUsers.emailSentSuccessfully"),
            showConfirmButton: false,
            timer: 2500,
          });
          this.$store.dispatch(names.FETCH_USER, {
            user_id: user.id,
            force: true,
          });
        });
    },
    currentPage(current_page) {
      this.current_page = current_page;
    },
    currentTag(current_tag) {
      this.current_tag = current_tag;
    },
    filterCustom(row) {
      if (
        this.$filtered(row.first_name, this.input_search) ||
        this.$filtered(row.last_name, this.input_search) ||
        this.$filtered(row.email, this.input_search) ||
        this.$filtered(this.getRole(row), this.input_search)
      ) {
        return true;
      } else {
        return false;
      }
    },
    filterUserByGroups(user) {
      const users = this.filtered_users.filter(
        (x) =>
          x.group_names.some((group) => user.group_names.includes(group)) &&
          x.id != user.id &&
          !x.is_superuser
      );
      return users;
    },
    // Copiar Permisos
    openModalCopyPermission(user) {
      this.$bvModal.show(`copy-user-permission-modal-${user.id}`);
      this.$store
        .dispatch(names.FETCH_ALT_USERS, {
          groups_ids: user.groups,
        })
        .then((response) => {
          this.filtered_users = response.filter(
            (response_user) =>
              user.group_names.some((group) =>
                response_user.group_names.includes(group)
              ) && response_user.id != user.id
          );
        });
    },
    getRole(user) {
      if (user.is_superuser == true)
        return this.$t("toolbar.adminUsers.superUser");
      const role = this.roles.find((x) => x.id == user.role);
      return role ? role.position_name : "";
    },

    openUserInfoModal(user_id) {
      this.$bvModal.show(`user-info-modal-${user_id}`);
    },
    askForDeactivateUser(user_item) {
      const is_active = user_item.is_active !== false;
      this.$swal({
        title: is_active
          ? this.$t("toolbar.adminUsers.deactivateUserConfirmTitle")
          : this.$t("toolbar.adminUsers.activateUserConfirmTitle"),
        text: is_active
          ? this.$t("toolbar.adminUsers.deactivateUserConfirmText")
          : this.$t("toolbar.adminUsers.activateUserConfirmText"),
        type: "warning",
        showCancelButton: true,
        confirmButtonText: is_active
          ? this.$t("toolbar.adminUsers.deactivateAction")
          : this.$t("toolbar.adminUsers.activateAction"),
        cancelButtonText: this.$t("reusable.common.cancel"),
      }).then((result) => {
        if (result.value) {
          this.$restful
            .Patch(`/authentication/user/${user_item.id}/`, {
              is_active: !is_active,
            })
            .then(() => {
              this.generic_table_key++;
              toast(
                is_active
                  ? this.$t("toolbar.adminUsers.userDeactivatedSuccessfully")
                  : this.$t("toolbar.adminUsers.userActivatedSuccessfully")
              );
            });
        }
      });
    },
    userIsInSection(item) {
      return (
        (item.sections_as_professor && item.sections_as_professor.length > 0) ||
        (item.sections_as_student && item.sections_as_student.length > 0)
      );
    },
    askForDeleteUser(user_id) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.$t("toolbar.adminUsers.user").toLowerCase(),
          article: "el",
        }),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.deleting_user_id = user_id;
          this.$swal.fire({
            title: this.$t("toolbar.adminUsers.deletingUser"),
            onBeforeOpen: () => {
              this.$swal.showLoading();
            },
            allowOutsideClick: false,
            allowEscapeKey: false,
          });
          this.$store
            .dispatch(names.DELETE_USER, user_id)
            .then(() => {
              this.fetchNumberOfUsersPerGroup();
              this.generic_table_key++;
              this.$swal.fire({
                type: "success",
                title: this.$t("toolbar.adminUsers.userDeletedSuccessfully"),
                showConfirmButton: false,
                timer: 2500,
              });
            })
            .catch((error) => {
              const msg =
                error?.response?.data?.detail ||
                "No se puede eliminar este usuario. Debe desactivarlo en su lugar.";
              this.$swal.fire({
                type: "error",
                title: "Error",
                text: msg,
              });
            })
            .finally(() => {
              this.deleting_user_id = null;
            });
        }
      });
    },
    copyTableAccess(from_user, to_user) {
      if (from_user && to_user && from_user != to_user) {
        this.$restful
          .Get(`teaching/copy_table_access/?from=${from_user}&to=${to_user}`)
          .then(() => {
            toast(this.$t("toolbar.adminUsers.permissionsCopiedSuccessfully"));
            this.$bvModal.hide(`copy-user-permission-modal-${to_user}`);
          });
      }
    },
    fetchNumberOfUsersPerGroup() {
      this.$restful
        .Post(`authentication/number_of_users_per_group/`, {
          group_names: [
            "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
            "DIRECTIVOS FACULTAD O ESCUELA",
            "DIRECTORES DE CARRERA",
            "ADMINISTRATIVOS INSTRUCCIONALES",
          ],
        })
        .then((response) => {
          this.group_count_users = response;
        });
    },
    slotCreatedUser() {
      this.fetchNumberOfUsersPerGroup();
      this.$bvModal.hide("new-user-modal");
      // Forzar recarga de datos de la tabla
      setTimeout(() => {
        if (this.$refs.genericTable) {
          this.generic_table_key++;
          this.$refs.genericTable.fetchData(this.current_page);
        }
      }, 300);
    },
    slotCloseModal(user) {
      this.fetchNumberOfUsersPerGroup();
      this.$bvModal.hide(`edit-user-modal-${user.id}`);
      // Forzar recarga de datos de la tabla
      setTimeout(() => {
        if (this.$refs.genericTable) {
          this.generic_table_key++;
          this.$refs.genericTable.fetchData(this.current_page);
        }
      }, 300);
    },
  },
  created() {
    this.fetchNumberOfUsersPerGroup();
  },
};
</script>

<style scoped>
.admin-users-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 1.5em;
  flex-wrap: wrap;
  margin-bottom: 1em;
  width: 100%;
}

.toolbar-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.toolbar-filter {
  flex: 1 1 260px;
  /* max-width: 420px; */
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
}

.filter-icon {
  color: var(--kl-color-accent);
}

.role-select {
  font-size: var(--kl-fs-body);
}

.role-select >>> .vs__dropdown-toggle {
  border: 1px solid var(--kl-input-border);
  border-radius: var(--kl-input-radius);
  background: var(--kl-surface);
  min-height: 32px;
  padding: 2px 4px;
}

.role-select >>> .vs__dropdown-toggle:focus-within {
  border-color: var(--kl-input-border-focus);
  box-shadow: var(--kl-shadow-focus);
}

.role-select >>> .vs__selected {
  background-color: var(--kl-chip-solid-bg);
  color: var(--kl-chip-solid-text);
  border: none;
  border-radius: var(--kl-chip-radius);
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw);
  padding: var(--kl-chip-padding);
  margin: 2px;
}

.role-select >>> .vs__deselect {
  filter: brightness(10);
}

.role-select >>> .vs__search::placeholder {
  color: var(--kl-text-muted);
  font-size: var(--kl-fs-small);
}

.filter-clear {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: var(--kl-fs-caption);
  color: var(--kl-color-accent);
  cursor: pointer;
  align-self: flex-start;
}

.filter-clear:hover {
  text-decoration: underline;
}

/* Avatar + inactive badge */
.avatar-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

.inactive-badge {
  font-size: 0.62rem;
  padding: 2px 5px;
  vertical-align: middle;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .admin-users-toolbar {
    flex-direction: column;
    gap: 0.75em;
  }
  .toolbar-filter {
    max-width: 100%;
    width: 100%;
  }
}
</style>

